import { reportUnexpectedClientError } from './client-error.service'


interface IAssetManifest {
    files?: Record<string, string | undefined>
}

const MAIN_SCRIPT_PATTERN = /\/static\/js\/main(?:\.[^/]+)?\.js$/i
const MAIN_STYLE_PATTERN = /\/static\/css\/main(?:\.[^/]+)?\.css$/i
let hasReportedManifestError = false

const reportManifestError = (error: unknown) => {
    if (hasReportedManifestError) return

    hasReportedManifestError = true
    reportUnexpectedClientError(error, 'ClientRelease.getLatestClientReleaseSignature')
}

const normalizeAssetPath = (value?: string | null) => {
    if (!value) return ''

    try {
        const url = new URL(value, window.location.origin)
        return `${url.pathname}${url.search}`
    } catch {
        return value.startsWith('/') ? value : `/${value}`
    }
}

const findLastMatchingAsset = (assets: Array<string>, pattern: RegExp) => {
    const matchedAssets = assets.filter((asset) => pattern.test(asset))

    return matchedAssets.length > 0 ? matchedAssets[matchedAssets.length - 1] : ''
}

const buildReleaseSignature = (mainScript: string, mainStyle: string) => {
    if (!mainScript && !mainStyle) return null

    return `${mainScript}::${mainStyle}`
}

const getManifestUrl = () => {
    const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '')
    return `${publicUrl}/asset-manifest.json?ts=${Date.now()}`
}

export const getCurrentClientReleaseSignature = () => {
    const scriptAssets = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]')).map((item) => normalizeAssetPath(item.getAttribute('src')))
    const styleAssets = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]')).map((item) => normalizeAssetPath(item.getAttribute('href')))

    const mainScript = findLastMatchingAsset(scriptAssets, MAIN_SCRIPT_PATTERN)
    const mainStyle = findLastMatchingAsset(styleAssets, MAIN_STYLE_PATTERN)

    return buildReleaseSignature(mainScript, mainStyle)
}

export const getLatestClientReleaseSignature = async (): Promise<string | null> => {
    try {
        const response = await fetch(getManifestUrl(), {
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
            },
        })

        if (!response.ok) {
            if (response.status >= 500) reportManifestError(new Error(`Asset manifest returned HTTP ${response.status}.`))
            return null
        }

        const manifest = await response.json() as IAssetManifest
        const mainScript = normalizeAssetPath(manifest.files?.['main.js'])
        const mainStyle = normalizeAssetPath(manifest.files?.['main.css'])

        return buildReleaseSignature(mainScript, mainStyle)
    } catch (error) {
        if (error instanceof SyntaxError) reportManifestError(error)
        return null
    }
}